/**
 * Hybrid Select Component
 * Automatically chooses between Tailwind and styled-components based on props.
 * Consumers passing styled-system props (e.g. `mb`, `width`, `bg`) get the
 * styled-components implementation; everyone else gets the Tailwind path.
 */

import * as React from "react";
import Icon from "@sproutsocial/seeds-react-icon";
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";
import Container, { Arrow } from "./styles";
import { SelectTailwind } from "./SelectTailwind";
import type { TypeSelectProps } from "./SelectTypes";

const Select = (props: TypeSelectProps) => {
  const {
    id,
    name,
    children,
    defaultValue,
    value,
    required,
    isInvalid,
    onChange,
    autoFocus,
    disabled,
    ariaLabel,
    ariaDescribedby,
    size = "default",
    qa = {},
    inputProps = {},
    ...rest
  } = props;

  const handleChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    if (onChange) {
      onChange(e);
    }
  };

  if (hasStyledProps(rest)) {
    return (
      <Container invalid={isInvalid} disabled={disabled} size={size} {...rest}>
        <select
          id={id}
          name={name}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedby}
          defaultValue={defaultValue}
          value={value}
          required={required}
          autoFocus={autoFocus}
          disabled={disabled}
          onChange={handleChange}
          data-qa-select={name || ""}
          data-qa-select-value={value || "unselected"}
          data-qa-select-isrequired={required === true}
          data-qa-select-isdisabled={disabled === true}
          {...qa}
          {...inputProps}
        >
          {children}
        </select>

        <Arrow size={size}>
          <Icon
            name="chevron-down-outline"
            fixedWidth
            size={size === "large" ? "medium" : "small"}
            aria-hidden
          />
        </Arrow>
      </Container>
    );
  }

  // Use Tailwind version (preferred - better performance)
  return <SelectTailwind {...props} />;
};

export default Select;
